import React from 'react';
import Modal from 'react-bootstrap/Modal';
import ModalHeader from 'react-bootstrap/ModalHeader';
import ModalTitle from 'react-bootstrap/ModalTitle';
import ModalBody from 'react-bootstrap/ModalBody';
import ModalFooter from 'react-bootstrap/ModalFooter';
import Button from 'react-bootstrap/Button';
import config from '../../../config';

const modalStyles = {
    modalBody: {
        maxHeight: '65vh',
        overflowY: 'auto',
        padding: '1.5rem',
        fontSize: '0.95rem',
        lineHeight: '1.6'
    },
    sectionTitle: {
        fontSize: '1.1rem',
        fontWeight: '600',
        color: '#2c3e50',
        marginTop: '1.5rem',
        marginBottom: '0.75rem',
        paddingBottom: '0.5rem',
        borderBottom: '2px solid #3498db'
    },
    subSectionTitle: {
        fontSize: '1rem',
        fontWeight: '600',
        color: '#34495e',
        marginTop: '1.25rem',
        marginBottom: '0.5rem'
    },
    paragraph: {
        marginBottom: '0.875rem',
        textAlign: 'justify'
    },
    list: {
        paddingLeft: '1.25rem',
        marginBottom: '1rem'
    },
    listItem: {
        marginBottom: '0.5rem'
    },
    centered: {
        textAlign: 'center',
        fontWeight: '500'
    },
    versionInfo: {
        color: '#7f8c8d',
        fontSize: '0.9rem',
        marginBottom: '1rem'
    },
    contactBlock: {
        backgroundColor: '#f8f9fa',
        padding: '1rem',
        borderRadius: '8px',
        marginBottom: '1rem'
    },
    link: {
        color: '#3498db',
        textDecoration: 'none'
    },
    divider: {
        margin: '1.5rem 0',
        border: 'none',
        borderTop: '1px solid #e0e0e0'
    },
    headerTitle: {
        textAlign: 'center',
        width: '100%'
    },
    modalHeader: {
        backgroundColor: '#f8f9fa',
        borderBottom: '2px solid #3498db'
    },
    modalFooter: {
        backgroundColor: '#f8f9fa',
        borderTop: '1px solid #e0e0e0',
        position: 'sticky',
        bottom: 0
    }
};

const PrivacyPolicyModal = (props) => {
    const clinicName = 'South Vancouver Medical Clinic';
    const websiteUrl = config.homepageUrl || 'SouthVanMed.com';
    const serviceEmail = 'info@SouthVanMed.com';
    const address = '350 SE Marine Drive, Vancouver, BC V5X 2S5';

    return (
        <Modal
            show={props.show}
            onHide={e => props.footerClick(e, false)}
            size="lg"
            centered
            scrollable
        >
            <ModalHeader closeButton style={modalStyles.modalHeader}>
                <ModalTitle style={modalStyles.headerTitle}>
                    <div style={{ fontSize: '1rem', color: '#7f8c8d' }}>{websiteUrl}</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#2c3e50' }}>PRIVACY POLICY</div>
                </ModalTitle>
            </ModalHeader>

            <ModalBody style={modalStyles.modalBody}>
                <p style={modalStyles.centered}>Please read this document completely.</p>
                <p style={modalStyles.centered}>
                    Continuing to use {websiteUrl} means that you agree with this Privacy Policy.
                </p>

                <p style={modalStyles.versionInfo}>Last updated: January 19, 2026 – Version 3.0</p>

                <p style={modalStyles.paragraph}>
                    Welcome to the {clinicName} portal (the "Website"). This Website includes {websiteUrl} and is operated by the {clinicName} ("{clinicName}," "we," "us," or "our") and our directly affiliated or subsidiary websites. Our Website allows, among other things, patients to book medical appointments with our physicians and receive other patient care services (the "Services"). Specific Services are described in greater detail below. Please read this document completely.
                </p>

                <p style={modalStyles.paragraph}>
                    This Privacy Policy is designed to be read in conjunction with our Terms of Use, which are available on the Website. By accessing the Website and/or using the Services, you agree to be bound by both our{' '}
                    <button className="button-link important-info-link font-1 m-0" name="terms" onClick={e => props.footerClick(e, true, true, 'showPrivacyPolicy')}>
                        Terms of Use
                    </button>{' '}
                    and this Privacy Policy. If you do not agree with this Privacy Policy or our Terms of Use, your only recourse is to cease using the Website and/or Services.
                </p>

                <p style={modalStyles.paragraph}>
                    The {clinicName} is committed to protecting the privacy of users of this Website (the "User," "you," or "your") and seeks to provide you with a safe and secure user experience. This Privacy Policy describes: our privacy practices; how we collect, use, disclose, and handle your personally identifiable information ("PII") during your use of or access to this Website and the Services; and the role you play in safeguarding your PII while using this Website and/or accessing the Services.
                </p>

                <p style={modalStyles.paragraph}>
                    This Privacy Policy and our practices regarding the collection, use, and disclosure of PII have been designed to comply with the Personal Information Protection Act, SBC 2003, c. 63 as amended from time to time ("PIPA"), and to meet or exceed the relevant guidelines and standards published by Doctors of BC, the College of Physicians and Surgeons of British Columbia, and the Office of the Information and Privacy Commissioner of British Columbia.
                </p>

                <p style={modalStyles.paragraph}>
                    Unless otherwise defined in this Privacy Policy, all capitalized terms have the meanings ascribed to them in the Terms of Use.
                </p>

                <hr style={modalStyles.divider} />

                {/* Section 1 */}
                <h3 style={modalStyles.sectionTitle}>1. HOW WE COLLECT, USE, AND DISCLOSE YOUR PERSONAL INFORMATION</h3>

                <p style={modalStyles.paragraph}>
                    The Website provides Users access to a secure online portal and platform (the "Portal") allowing them to receive Services from one of our physicians licensed to practice medicine in the Province of British Columbia and our support staff. The Services are only accessible to Users who have provided certain PII and other identification information required to create a patient user account (the "User Account") and who have agreed to this Privacy Policy and the Terms of Use.
                </p>

                <p style={modalStyles.paragraph}>
                    When you create a User Account and/or use our Services, we collect certain information about your device, your interaction with the Website and/or Portal, and information necessary to provide our Services.
                </p>

                <p style={modalStyles.paragraph}>
                    When you create a User Account, we collect information to verify your identity. Your User Account must not be accessed by anyone other than the person who created it. However, a parent or guardian of a User who is under the age of majority may access a User Account on behalf of that User if necessary.
                </p>

                <p style={modalStyles.paragraph}>
                    To optimize our Website, Portal, and Services and to provide a better user experience, we may collect certain User Data about you:
                </p>

                <ul style={modalStyles.list}>
                    <li style={modalStyles.listItem}>
                        <strong>Personal Data:</strong> voluntarily provided information which may include your name, address, email address, billing and/or credit card information, and other PII;
                    </li>
                    <li style={modalStyles.listItem}>
                        <strong>Aggregate Information:</strong> non-personal, anonymous demographic information or other information automatically collected when visiting our Services, which may include cookies, third-party tracking technologies, server logs, IP addresses, and device information such as operating system or browser type.
                    </li>
                </ul>

                <p style={modalStyles.paragraph}>
                    The {clinicName} uses this Website and/or the Portal to collect, and may use and disclose, your Personal Data, including personal health information, in order to deliver our Services. Our Services include, but are not limited to: allowing Users to book medical appointments; communicating with Users regarding patient care needs; providing recommendations; and sharing test results with Users.
                </p>

                <p style={modalStyles.paragraph}>
                    We will only collect Personal Data that you knowingly and willingly provide to us.
                </p>

                <p style={modalStyles.paragraph}>
                    We utilize the third-party payment processor Stripe to collect payments for some of our Services. For more information please see Stripe's Privacy Policy at{' '}
                    <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" style={modalStyles.link}>
                        https://stripe.com/privacy
                    </a>.
                </p>

                <p style={modalStyles.paragraph}>
                    We may disclose your Personal Data to you and to other health professionals only to the extent required to provide Services. Such communication may occur via email. While we will take reasonable steps to secure information transmitted by email, you understand that email carries certain risks and you agree to assume those risks.
                </p>

                <p style={modalStyles.paragraph}>
                    We will not sell your Personal Data to third parties.
                </p>

                <hr style={modalStyles.divider} />

                {/* Section 1A - NEW Virtual Care Consent */}
                <h3 style={modalStyles.sectionTitle}>1A. VIRTUAL CARE CONSENT</h3>

                <h4 style={modalStyles.subSectionTitle}>Provision of Care by Virtual Means</h4>
                <p style={modalStyles.paragraph}>
                    Some Services may be delivered using virtual care technologies including secure messaging, email, telephone, and videoconferencing ("Virtual Care"). Virtual Care may be used for consultations, follow-up visits, sharing of results, and other services where clinically appropriate.
                </p>

                <h4 style={modalStyles.subSectionTitle}>Your Consent</h4>
                <p style={modalStyles.paragraph}>
                    By using the Website, Portal, or any Virtual Care Services, you acknowledge and consent that:
                </p>
                <ul style={modalStyles.list}>
                    <li style={modalStyles.listItem}>Personal and health information will be communicated electronically;</li>
                    <li style={modalStyles.listItem}>Information will be collected, used, and disclosed for assessment, diagnosis, treatment, referrals, and administration;</li>
                    <li style={modalStyles.listItem}>Virtual Care may not be appropriate for all conditions and an in-person visit may be required;</li>
                    <li style={modalStyles.listItem}>Quality of care may be affected by internet or technical limitations.</li>
                </ul>

                <h4 style={modalStyles.subSectionTitle}>Risks</h4>
                <p style={modalStyles.paragraph}>
                    You understand that Virtual Care involves risks including:
                </p>
                <ul style={modalStyles.list}>
                    <li style={modalStyles.listItem}>Interruption or unauthorized access to electronic communications;</li>
                    <li style={modalStyles.listItem}>Limits to diagnosis without physical examination;</li>
                    <li style={modalStyles.listItem}>Delays due to technology failure.</li>
                </ul>
                <p style={modalStyles.paragraph}>
                    You accept these risks by choosing Virtual Care.
                </p>

                <h4 style={modalStyles.subSectionTitle}>Security</h4>
                <p style={modalStyles.paragraph}>
                    We use encrypted platforms and authentication procedures. You agree to:
                </p>
                <ul style={modalStyles.list}>
                    <li style={modalStyles.listItem}>Use a private location for appointments;</li>
                    <li style={modalStyles.listItem}>Ensure that no unauthorized person can view or hear the visit;</li>
                    <li style={modalStyles.listItem}>Use secure internet rather than public networks where possible.</li>
                </ul>

                <h4 style={modalStyles.subSectionTitle}>Recording</h4>
                <p style={modalStyles.paragraph}>
                    You will not record any Virtual Care interaction without express written consent. The Clinic will not record visits without informing you and obtaining consent where required.
                </p>

                <h4 style={modalStyles.subSectionTitle}>Withdrawal</h4>
                <p style={modalStyles.paragraph}>
                    You may withdraw consent at any time by notifying us. Withdrawal may require in-person care.
                </p>

                <h4 style={modalStyles.subSectionTitle}>Emergencies</h4>
                <p style={modalStyles.paragraph}>
                    <strong>Virtual Care is not for medical emergencies. Call 911 or attend the nearest emergency department for urgent concerns.</strong>
                </p>

                <hr style={modalStyles.divider} />

                {/* Section 2 */}
                <h3 style={modalStyles.sectionTitle}>2. HOW WE WORK WITH YOU TO SAFEGUARD YOUR PERSONAL INFORMATION</h3>

                <p style={modalStyles.paragraph}>
                    We employ safeguards including firewalls, encryption (HTTPS/SSL), authentication procedures, anti-malware software, audit trails, and secure Canadian cloud storage. Employees may access Personal Data only as necessary.
                </p>

                <p style={modalStyles.paragraph}>
                    You should:
                </p>
                <ul style={modalStyles.list}>
                    <li style={modalStyles.listItem}>Use secure internet when accessing your Account;</li>
                    <li style={modalStyles.listItem}>Not save passwords on shared devices;</li>
                    <li style={modalStyles.listItem}>Not share passwords;</li>
                    <li style={modalStyles.listItem}>Notify us of unauthorized access;</li>
                    <li style={modalStyles.listItem}>Limit Personal Data shared via email or text;</li>
                    <li style={modalStyles.listItem}>Log out after each session.</li>
                </ul>

                <hr style={modalStyles.divider} />

                {/* Section 3 */}
                <h3 style={modalStyles.sectionTitle}>3. YOUR CONSENT</h3>

                <p style={modalStyles.paragraph}>
                    By using the Website and Services, you consent to our collection, use, and disclosure of your Personal Data in accordance with this Policy. You may withdraw consent at any time in writing; this may limit our ability to provide Services.
                </p>

                <hr style={modalStyles.divider} />

                {/* Section 4 */}
                <h3 style={modalStyles.sectionTitle}>4. ACCESS AND MODIFICATION</h3>

                <p style={modalStyles.paragraph}>
                    You have the right to access your Personal Data, subject to legal exceptions. Requests should be directed to our Privacy Officer. You may update information through your User Account or by contacting us.
                </p>

                <hr style={modalStyles.divider} />

                {/* Section 5 */}
                <h3 style={modalStyles.sectionTitle}>5. RECORD RETENTION</h3>

                <p style={modalStyles.paragraph}>
                    We retain personal health information for at least sixteen (16) years from the last entry or from the date the patient reaches the age of majority, whichever is later, in accordance with PIPA and regulatory requirements. After retention periods expire, data will be securely destroyed.
                </p>

                <hr style={modalStyles.divider} />

                {/* Section 6 */}
                <h3 style={modalStyles.sectionTitle}>6. PRIVACY BREACH RESPONSE</h3>

                <p style={modalStyles.paragraph}>
                    If a breach occurs, we will investigate and notify affected individuals and authorities as required by PIPA.
                </p>

                <hr style={modalStyles.divider} />

                {/* Section 7 */}
                <h3 style={modalStyles.sectionTitle}>7. CONCERNS OR COMPLAINTS</h3>

                <div style={modalStyles.contactBlock}>
                    <p style={{ marginBottom: '0.5rem' }}><strong>Privacy Officer</strong></p>
                    <p style={{ marginBottom: '0.25rem' }}>Attention: Ying Pang, Privacy Officer</p>
                    <p style={{ marginBottom: '0.25rem' }}>Email: {serviceEmail}</p>
                    <p style={{ marginBottom: '0' }}>Address: {address}</p>
                </div>

                <div style={modalStyles.contactBlock}>
                    <p style={{ marginBottom: '0.5rem' }}><strong>Office of the Information and Privacy Commissioner for BC</strong></p>
                    <p style={{ marginBottom: '0.25rem' }}>
                        Website:{' '}
                        <a href="https://www.oipc.bc.ca" target="_blank" rel="noopener noreferrer" style={modalStyles.link}>
                            www.oipc.bc.ca
                        </a>
                    </p>
                    <p style={{ marginBottom: '0.25rem' }}>Phone: (250) 387-5629</p>
                    <p style={{ marginBottom: '0' }}>Email: info@oipc.bc.ca</p>
                </div>

                <hr style={modalStyles.divider} />

                {/* Section 8 */}
                <h3 style={modalStyles.sectionTitle}>8. UPDATES TO THIS POLICY</h3>

                <p style={modalStyles.paragraph}>
                    We may modify this Privacy Policy at any time. Continued use constitutes acceptance of changes. The current version is available on our Website or upon request.
                </p>

                <p style={modalStyles.paragraph}>
                    This Privacy Policy does not create rights beyond those provided under applicable privacy laws. In case of inconsistency, applicable laws will prevail.
                </p>
            </ModalBody>

            <ModalFooter style={modalStyles.modalFooter}>
                <Button
                    name="privacy-policy"
                    variant="primary"
                    onClick={e => props.footerClick(e, false)}
                    style={{ minWidth: '100px' }}
                >
                    Close
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default PrivacyPolicyModal;
